import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMuiMenu } from "@shared/frontend/hooks/use-mui-menu.hook";
import type { CommunicationRecord } from "@entities";
import { type FC } from "react";
import { CommunicationRecordsTableCell } from "../../../components/CommunicationRecordManager/CommunicationRecordsTableCell";
import { LeadHandlingControls } from "../../../components/lead-handling/LeadHandlingControls";
import { useUser } from "../../../contexts/UserProvider";
import PhoneNumberCell from "../PhoneNumberCell";
import { LeadContactStatusSelect } from "./LeadContactStatusSelect";
import { LeadStatusChip } from "./LeadStatusChip";
import { LeadActionTooltip } from "./LeadActionTooltip";
import {
  LEAD_ACTION_TOOLTIPS,
  treatButtonTooltip,
} from "./lead-actions-copy";
import type { SummerCampaignLeadRow } from "./helpers";

type Props = {
  lead: SummerCampaignLeadRow;
  onTreatAndRegister: (lead: SummerCampaignLeadRow) => void | Promise<void>;
  onCommunication: (lead: SummerCampaignLeadRow) => void;
  onDecline: (lead: SummerCampaignLeadRow) => void;
  onContactStatusUpdated?: () => void;
  onCommunicationSaved?: (records?: CommunicationRecord[]) => void;
  onCallInitiated?: (leadId: string) => void;
  commLoading?: boolean;
  registerLoading?: boolean;
};

function formatDate(value: string | Date | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("he-IL");
}

function ChipList({ items, max = 2 }: { items: string[]; max?: number }) {
  if (!items.length) return <Typography variant="body2">—</Typography>;
  const shown = items.slice(0, max);
  const rest = items.length - shown.length;
  return (
    <Box className="leads-table__prefs-chips">
      {shown.map((item) => (
        <Chip key={item} size="small" label={item} variant="outlined" />
      ))}
      {rest > 0 && <Chip size="small" label={`+${rest}`} />}
    </Box>
  );
}

export const LeadTableRow: FC<Props> = ({
  lead,
  onTreatAndRegister,
  onCommunication,
  onDecline,
  onContactStatusUpdated,
  onCommunicationSaved,
  onCallInitiated,
  commLoading,
  registerLoading,
}) => {
  const { currentUser } = useUser();
  const [menuRef, menuProps, menuActions] = useMuiMenu<HTMLButtonElement>();

  const isMine =
    !!lead.handlingManagerId && lead.handlingManagerId === currentUser?.id;
  const isOther =
    !!lead.handlingManagerId && lead.handlingManagerId !== currentUser?.id;
  const isNew = lead.status === "SUBMITTED";

  const rowClass = [
    isMine ? "leads-table__row--mine" : "",
    isOther ? "leads-table__row--other" : "",
    !lead.contactConsent ? "leads-table__row--no-consent" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const canRegister =
    lead.contactConsent && lead.status !== "DECLINED" && !isOther;

  return (
    <TableRow hover className={rowClass}>
      <TableCell>
        <Stack gap={0.25}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            {isNew && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "info.main",
                  flexShrink: 0,
                }}
              />
            )}
            <PhoneNumberCell
              phoneNumber={lead.phoneNumber}
              onCallInitiated={() => onCallInitiated?.(lead.id)}
            />
            {!lead.contactConsent && (
              <Tooltip title={LEAD_ACTION_TOOLTIPS.noConsentWarning} arrow>
                <WarningAmberIcon color="warning" sx={{ fontSize: "1rem" }} />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            נכנס {formatDate(lead.submittedAt)}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Stack gap={0.75}>
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            <Chip size="small" label={lead.regionLabel} />
            <Chip size="small" label={lead.attendanceLabel} variant="outlined" />
          </Stack>
          <ChipList items={lead.teamNames} />
          <ChipList items={lead.categoryNames} />
          <Stack direction="row" gap={0.5} alignItems="center">
            <CalendarMonthIcon sx={{ fontSize: "0.9rem", color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              {formatDate(lead.availabilityStart)} - {formatDate(lead.availabilityEnd)}
            </Typography>
          </Stack>
        </Stack>
      </TableCell>
      <TableCell>
        <Stack gap={0.5}>
          <LeadStatusChip status={lead.status} />
          <LeadContactStatusSelect
            leadId={lead.id}
            contactStatus={lead.contactStatus}
            disabled={!lead.contactConsent}
            onUpdated={onContactStatusUpdated}
          />
          <Typography variant="caption" color="text.secondary">
            {lead.registrationCount} רישומים
          </Typography>
        </Stack>
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        {lead.contactConsent ? (
          <CommunicationRecordsTableCell
            records={lead.communicationRecords as CommunicationRecord[] | undefined}
            summerCampaignLeadId={lead.id}
            associationId={lead.associationId}
            title={lead.phoneNumber}
            onSave={onCommunicationSaved ?? onContactStatusUpdated}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            אין הסכמה
          </Typography>
        )}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <LeadHandlingControls
          leadId={lead.id}
          handlingManagerId={lead.handlingManagerId}
          handlingManagerName={lead.handlingManagerName}
          handlingSince={lead.handlingSince}
          variant="compact"
        />
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Stack direction="row" gap={0.5} alignItems="center">
          <LeadActionTooltip
            title={treatButtonTooltip({
              isMine,
              isOther,
              hasConsent: lead.contactConsent,
              managerName: lead.handlingManagerName,
            })}
          >
            <Button
              size="small"
              variant="contained"
              color="secondary"
              className="leads-table__cta"
              disabled={!canRegister || registerLoading}
              onClick={() => onTreatAndRegister(lead)}
            >
              {isMine ? "המשך רישום" : "טיפול ורישום"}
            </Button>
          </LeadActionTooltip>
          <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.moreActions}>
            <IconButton
              size="small"
              ref={menuRef}
              onClick={() => menuActions.toggle()}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </LeadActionTooltip>
          <Menu {...menuProps}>
            <MenuItem
              disabled={!lead.contactConsent || commLoading}
              title={
                lead.contactConsent
                  ? LEAD_ACTION_TOOLTIPS.communication
                  : LEAD_ACTION_TOOLTIPS.communicationDisabled
              }
              onClick={() => {
                menuActions.hide();
                onCommunication(lead);
              }}
            >
              {commLoading ? "טוען..." : "תיעוד שיחה"}
            </MenuItem>
            {lead.status !== "DECLINED" && (
              <MenuItem
                title={LEAD_ACTION_TOOLTIPS.decline}
                onClick={() => {
                  menuActions.hide();
                  onDecline(lead);
                }}
              >
                לא לטפל
              </MenuItem>
            )}
          </Menu>
        </Stack>
      </TableCell>
    </TableRow>
  );
};
